/**
 * Menu Extraction Slice
 * Redux state management for AI menu extraction
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MenuExtraction, MenuExtractionState } from '../../types/menu';
import {
  commitExtraction,
  createMenuExtraction,
  declineAndRerunExtraction,
  fetchExtractions,
  fetchPendingExtractions,
  processExtractionWithAI,
  saveExtractionEdits,
  updateMenuExtraction,
} from './menuExtractionThunks';

const initialState: MenuExtractionState = {
  extractions: [],
  currentExtraction: null,
  isExtracting: false,
  isCommitting: false,
  error: null,
};

const menuExtractionSlice = createSlice({
  name: 'menuExtractions',
  initialState,
  reducers: {
    setCurrentExtraction: (
      state,
      action: PayloadAction<MenuExtraction | null>,
    ) => {
      state.currentExtraction = action.payload;
    },
    clearExtractionError: (state) => {
      state.error = null;
    },
    resetMenuExtractions: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch pending extractions
    builder.addCase(fetchPendingExtractions.pending, (state) => {
      state.error = null;
    });
    builder.addCase(fetchPendingExtractions.fulfilled, (state, action) => {
      state.extractions = action.payload;
      // Set current extraction to the most recent pending one
      if (action.payload.length > 0 && !state.currentExtraction) {
        // eslint-disable-next-line prefer-destructuring
        state.currentExtraction = action.payload[0];
      }
    });
    builder.addCase(fetchPendingExtractions.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Fetch all extractions
    builder.addCase(fetchExtractions.pending, (state) => {
      state.error = null;
    });
    builder.addCase(fetchExtractions.fulfilled, (state, action) => {
      state.extractions = action.payload;
    });
    builder.addCase(fetchExtractions.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Create extraction
    builder.addCase(createMenuExtraction.pending, (state) => {
      state.isExtracting = true;
      state.error = null;
    });
    builder.addCase(createMenuExtraction.fulfilled, (state, action) => {
      state.extractions.unshift(action.payload);
      state.currentExtraction = action.payload;
      state.isExtracting = false;
    });
    builder.addCase(createMenuExtraction.rejected, (state, action) => {
      state.isExtracting = false;
      state.error = action.payload as string;
    });

    // Process with AI
    builder.addCase(processExtractionWithAI.pending, (state) => {
      state.isExtracting = true;
      state.error = null;
    });
    builder.addCase(processExtractionWithAI.fulfilled, (state, action) => {
      state.isExtracting = false;
      // Update the extraction in the list and current
      const index = state.extractions.findIndex(
        (e) => e.id === action.payload.extractionId,
      );
      if (index !== -1) {
        state.extractions[index].json_output = action.payload.output;
        state.extractions[index].status = 'pending';
      }
      if (state.currentExtraction?.id === action.payload.extractionId) {
        state.currentExtraction.json_output = action.payload.output;
        state.currentExtraction.status = 'pending';
      }
    });
    builder.addCase(processExtractionWithAI.rejected, (state, action) => {
      state.isExtracting = false;
      state.error = action.payload as string;
    });

    // Update extraction
    builder.addCase(updateMenuExtraction.fulfilled, (state, action) => {
      const index = state.extractions.findIndex(
        (e) => e.id === action.payload.id,
      );
      if (index !== -1) {
        state.extractions[index] = action.payload;
      }
      if (state.currentExtraction?.id === action.payload.id) {
        state.currentExtraction = action.payload;
      }
    });
    builder.addCase(updateMenuExtraction.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Commit extraction
    builder.addCase(commitExtraction.pending, (state) => {
      state.isCommitting = true;
      state.error = null;
    });
    builder.addCase(commitExtraction.fulfilled, (state, action) => {
      state.isCommitting = false;
      // Update status to committed
      const index = state.extractions.findIndex(
        (e) => e.id === action.payload.extractionId,
      );
      if (index !== -1) {
        state.extractions[index].status = 'committed';
      }
      if (state.currentExtraction?.id === action.payload.extractionId) {
        state.currentExtraction.status = 'committed';
      }
    });
    builder.addCase(commitExtraction.rejected, (state, action) => {
      state.isCommitting = false;
      state.error = action.payload as string;
    });

    // Decline and rerun
    builder.addCase(declineAndRerunExtraction.pending, (state) => {
      state.isExtracting = true;
      state.error = null;
    });
    builder.addCase(declineAndRerunExtraction.fulfilled, (state, action) => {
      state.isExtracting = false;
      state.extractions.unshift(action.payload);
      state.currentExtraction = action.payload;
    });
    builder.addCase(declineAndRerunExtraction.rejected, (state, action) => {
      state.isExtracting = false;
      state.error = action.payload as string;
    });

    // Save edits
    builder.addCase(saveExtractionEdits.fulfilled, (state, action) => {
      const index = state.extractions.findIndex(
        (e) => e.id === action.payload.id,
      );
      if (index !== -1) {
        state.extractions[index] = action.payload;
      }
      if (state.currentExtraction?.id === action.payload.id) {
        state.currentExtraction = action.payload;
      }
    });
    builder.addCase(saveExtractionEdits.rejected, (state, action) => {
      state.error = action.payload as string;
    });
  },
});

export const {
  setCurrentExtraction,
  clearExtractionError,
  resetMenuExtractions,
} = menuExtractionSlice.actions;

export default menuExtractionSlice.reducer;
